import { createAsyncThunk } from '@reduxjs/toolkit';
import { useQuery, UseQueryOptions } from 'react-query';
import { useDispatch } from 'react-redux';
import accountService from 'services/accountService';
import { FullScribeUser } from 'v2/types';

export type AccountInfoResponse = FullScribeUser;

/**
 * A Redux async thunk for fetching the current account information.
 *
 * This is imported by a few slices so we have it in it's own file.
 */
export const getAccountInfo = createAsyncThunk<FullScribeUser>('account/getInfo', async (_, { rejectWithValue }) => {
  try {
    return (await accountService.getInfo()) as FullScribeUser;
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const AccountInfoQueryKey = 'current_user_account_info';
/**
 * A hook for retrieving the current account information.
 *
 * Updates Redux state when fetching account info.
 */
export function useAccountInfo(override?: UseQueryOptions<AccountInfoResponse>) {
  const dispatch = useDispatch();

  const account = useQuery<AccountInfoResponse>({
    queryKey: AccountInfoQueryKey,
    queryFn: async () => {
      dispatch(getAccountInfo.pending(null));

      try {
        const accountInfo = (await accountService.getInfo()) as FullScribeUser;

        // @ts-expect-error - Should be solved when migrated to React-Query
        dispatch(getAccountInfo.fulfilled(accountInfo));

        return { ...accountInfo };
      } catch (err) {
        // @ts-expect-error - Should be solved when migrated to React-Query
        dispatch(getAccountInfo.rejected(err));

        throw err;
      }
    },
    ...override
  });

  return account;
}

export default useAccountInfo;
